import { updateBrandingSchema, type UpdateBrandingInput } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FormField } from '@/components/FormField';
import { ImageUpload } from '@/components/ImageUpload';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { useLocalMutation } from './useUpdateLocal';

/** Texto blanco o negro según la luminancia del fondo. */
function readableText(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return '#ffffff';
  const n = parseInt(m[1]!, 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  const lum = 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
  return lum > 0.45 ? '#111111' : '#ffffff';
}

function ColorField({ value, onChange, disabled, id }: { value: string; onChange: (v: string) => void; disabled?: boolean; id: string }) {
  return (
    <div className="flex items-center gap-2">
      <input
        type="color"
        aria-label="Elegir color"
        value={/^#[0-9a-f]{6}$/i.test(value) ? value : '#000000'}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-10 w-12 cursor-pointer rounded-md border bg-transparent p-1"
      />
      <Input id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} className="font-mono uppercase" />
    </div>
  );
}

export function AparienciaPage() {
  const local = useCurrentLocal();
  const canEdit = useHasRole('OWNER', 'MANAGER');
  const [uploading, setUploading] = useState(false);

  const form = useForm({
    resolver: zodResolver(updateBrandingSchema),
    defaultValues: {
      logoUrl: local.logoUrl ?? '',
      primaryColor: local.primaryColor,
      secondaryColor: local.secondaryColor,
      hideBranding: local.hideBranding,
    },
  });
  const { errors, isDirty } = form.formState;
  const values = useWatch({ control: form.control });
  const save = useLocalMutation((body: UpdateBrandingInput) => api.locals.updateBranding(local.id, body));

  const primary = values.primaryColor ?? local.primaryColor;
  const secondary = values.secondaryColor ?? local.secondaryColor;
  const logo = values.logoUrl;

  return (
    <form
      className="grid gap-6 lg:grid-cols-[1fr_320px]"
      onSubmit={form.handleSubmit((v) => save.mutate(v, { onSuccess: () => form.reset(v) }))}
      noValidate
    >
      <Card>
        <CardHeader>
          <CardTitle>Apariencia del menú</CardTitle>
          <CardDescription>Logo y colores de tu marca.</CardDescription>
        </CardHeader>
        <fieldset disabled={!canEdit} className="contents">
          <CardContent className="grid gap-4">
            <FormField
              label="Logo"
              htmlFor="logoUrl"
              error={errors.logoUrl?.message}
              hint="Mejor si es cuadrado. Se ve arriba de todo en tu menú."
            >
              <Controller
                control={form.control}
                name="logoUrl"
                render={({ field }) => (
                  <ImageUpload
                    id="logoUrl"
                    shape="square"
                    localId={local.id}
                    value={field.value ?? ''}
                    onChange={(url) => field.onChange(url)}
                    error={errors.logoUrl?.message}
                    onBusyChange={setUploading}
                    disabled={!canEdit}
                  />
                )}
              />
            </FormField>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField label="Color principal" htmlFor="primaryColor" error={errors.primaryColor?.message}>
                <Controller
                  control={form.control}
                  name="primaryColor"
                  render={({ field }) => (
                    <ColorField id="primaryColor" value={field.value ?? ''} onChange={field.onChange} disabled={!canEdit} />
                  )}
                />
              </FormField>
              <FormField label="Color secundario" htmlFor="secondaryColor" error={errors.secondaryColor?.message}>
                <Controller
                  control={form.control}
                  name="secondaryColor"
                  render={({ field }) => (
                    <ColorField id="secondaryColor" value={field.value ?? ''} onChange={field.onChange} disabled={!canEdit} />
                  )}
                />
              </FormField>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
              <div>
                <p className="text-sm font-medium">Ocultar “Hecho con Gestión Locales”</p>
                <p className="text-xs text-muted-foreground">Saca el pie de página del menú público.</p>
              </div>
              <Controller
                control={form.control}
                name="hideBranding"
                render={({ field }) => (
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} disabled={!canEdit} />
                )}
              />
            </div>
          </CardContent>
        </fieldset>
        {canEdit && (
          <CardFooter className="justify-end">
            <Button type="submit" disabled={!isDirty || save.isPending || uploading}>
              {save.isPending && <Spinner />} Guardar
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* Preview simple */}
      <div className="lg:sticky lg:top-8 lg:self-start">
        <p className="mb-2 text-sm font-medium text-muted-foreground">Vista previa</p>
        <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
          <div className="flex items-center gap-3 p-4" style={{ backgroundColor: primary, color: readableText(primary) }}>
            {logo ? (
              <img src={logo} alt="" className="size-12 rounded-xl bg-white object-cover" />
            ) : (
              <span className="flex size-12 items-center justify-center rounded-xl bg-white/20 text-lg font-bold">
                {local.name.slice(0, 1)}
              </span>
            )}
            <div>
              <p className="font-semibold">{local.name}</p>
              <p className="text-xs opacity-80">Abierto ahora</p>
            </div>
          </div>
          <div className="flex gap-2 p-3">
            {['Helados', 'Cafetería', 'Postres'].map((c, i) => (
              <span
                key={c}
                className="rounded-full px-3 py-1 text-xs font-medium"
                style={
                  i === 0
                    ? { backgroundColor: primary, color: readableText(primary) }
                    : { backgroundColor: secondary, color: readableText(secondary) }
                }
              >
                {c}
              </span>
            ))}
          </div>
          <div className="space-y-3 p-4 pt-1 text-neutral-900">
            {[
              ['Helado 1/4 kg', '$ 6.500'],
              ['Cucurucho 2 bochas', '$ 4.800'],
            ].map(([n, p]) => (
              <div key={n} className="flex justify-between border-b border-neutral-100 pb-2 text-sm">
                <span>{n}</span>
                <span className="font-semibold" style={{ color: primary }}>
                  {p}
                </span>
              </div>
            ))}
            {!values.hideBranding && <p className="pt-2 text-center text-[11px] text-neutral-400">Hecho con Gestión Locales</p>}
          </div>
        </div>
      </div>
    </form>
  );
}
